import Image from 'next/image';
import Link from 'next/link';
import { format } from 'date-fns';
import { MAJOR_BRANDS } from '@/lib/category-hierarchy';
import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2, X, Heart, Eye, Share2, Check, Copy, Facebook, Linkedin, Twitter, Activity, Flag, Instagram, MoreVertical } from 'lucide-react';
import { deleteAuthorPost, getAllCategoriesForEditor, getPostContentById, incrementView, toggleLike, incrementImpression, incrementShare, reportContent, updateAuthorPost } from '@/app/actions/public-actions';
import { useInView } from 'react-intersection-observer';
import { sendGAEvent } from '@next/third-parties/google';
import CommentsThread from './CommentsThread';
import { getCanCurrentSessionComment } from '@/app/actions/comment-actions';
import ImageCarousel, { type CarouselImage } from './ImageCarousel';
import { resolveAltText } from '@/lib/image-utils';

const enhanceHtmlOutput = (html: string) => {
    if (!html) return html;

    let processed = html.replace(
        /(^|[\s>])(#([a-zA-Z0-9_]+))/g,
        (match, prefix: string, fullTag: string, tag: string) => {
            const href = `/articles?q=${encodeURIComponent(`#${tag}`)}`;
            return `${prefix}<a href="${href}" class="!text-[var(--color-primary)] hover:underline">${fullTag}</a>`;
        }
    );

    processed = processed.replace(
        /src=["'](?!\/|http)([^"']+\.(?:jpg|jpeg|png|webp|avif|gif))["']/gi,
        'src="/api/media/$1"'
    );
    
    return processed;
};

const extractYouTubeVideoId = (input: string): string | null => {
    const raw = String(input || '').trim();
    if (!raw) return null;
    let url: URL | null = null;
    try {
        url = new URL(raw);
    } catch {
        try {
            url = new URL(`https://${raw}`);
        } catch {
            url = null;
        }
    }
    if (!url) return null;

    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    const parts = url.pathname.split('/').filter(Boolean);
    const isValidId = (id: string) => /^[a-zA-Z0-9_-]{11}$/.test(id);

    if (host === 'youtu.be') {
        const id = parts[0] || '';
        return isValidId(id) ? id : null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com' || host.endsWith('.youtube.com')) {
        if (url.pathname === '/watch') {
            const id = url.searchParams.get('v') || '';
            return isValidId(id) ? id : null;
        }
        const first = parts[0] || '';
        const second = parts[1] || '';
        if (first === 'embed' || first === 'shorts' || first === 'live' || first === 'v') {
            const id = second || '';
            return isValidId(id) ? id : null;
        }
    }

    return null;
};

const buildYouTubeEmbedUrl = (videoId: string) => {
    const id = String(videoId || '').trim();
    if (!id) return '';
    const params = new URLSearchParams({
        autoplay: '0',
        mute: '0',
        controls: '1',
        modestbranding: '1',
        playsinline: '1',
        rel: '0',
        origin: 'https://carsite.co.za',
        widget_referrer: 'https://carsite.co.za',
    });
    return `https://www.youtube.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
};

interface PostCardProps {
    post: {
        id: string;
        title: string;
        slug: string;
        excerpt?: string;
        published_at?: string | Date | null;
        author_name?: string | null;
        author_handle?: string | null;
        author_display_name?: string | null;
        author_avatar_url?: string | null;
        data?: any;
        views?: number;
        likes?: number;
        impressions?: number;
        shares?: number;
    };
    categoryLabel?: string;
    className?: string;
    enableEdit?: boolean;
}

export default function PostCard({ post, categoryLabel, className = '', enableEdit }: PostCardProps) {
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const [content, setContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [canComment, setCanComment] = useState(false);

    // Interaction State
    const [likes, setLikes] = useState(post.likes || 0);
    const [views, setViews] = useState(post.views || 0);
    const [shares, setShares] = useState(post.shares || 0);
    const [impressions, setImpressions] = useState(post.impressions || 0);
    const [isLiked, setIsLiked] = useState(false);

    // Share State
    const [shareMenu, setShareMenu] = useState<'top' | 'footer' | null>(null);
    const [copied, setCopied] = useState(false);
    const topShareMenuRef = useRef<HTMLDivElement>(null);
    const footerShareMenuRef = useRef<HTMLDivElement>(null);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const topMoreMenuRef = useRef<HTMLDivElement>(null);

    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editTitle, setEditTitle] = useState(String(post.title || ''));
    const [editBody, setEditBody] = useState(
        String(post.data?.content?.body || post.excerpt || '')
    );
    const [editCategories, setEditCategories] = useState<Array<{ slug: string; name: string }>>(() => {
        const raw = post.data?.taxonomy?.categories;
        if (!Array.isArray(raw)) return [];
        const out: Array<{ slug: string; name: string }> = [];
        const seen = new Set<string>();
        for (const c of raw) {
            const slug = typeof c?.slug === 'string' ? c.slug.trim() : '';
            if (!slug) continue;
            const key = slug.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            const name = typeof c?.name === 'string' && c.name.trim().length > 0 ? c.name.trim() : slug;
            out.push({ slug, name });
        }
        return out;
    });
    const [editFeaturedImageUrl, setEditFeaturedImageUrl] = useState<string | null>(() => {
        const raw = post.data?.featured_media?.url as string | undefined;
        const has = typeof raw === 'string' && raw.trim().length > 0;
        return has ? raw.trim().replace(/^['"]|['"]$/g, '') : null;
    });
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [isUploadingEditImage, setIsUploadingEditImage] = useState(false);
    const editFileInputRef = useRef<HTMLInputElement>(null);

    const [allCategories, setAllCategories] = useState<Array<{ slug: string; name: string }>>([]);
    const [categoryInput, setCategoryInput] = useState('');
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);

    const { ref: inViewRef, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    const carouselImages: CarouselImage[] = (() => {
        const out: CarouselImage[] = [];
        const media = post.data?.media;
        const urls = Array.isArray(media?.images) ? media.images : [];
        const meta = Array.isArray(media?.image_meta) ? media.image_meta : [];
        const altByUrl = new Map<string, string>();
        
        const resolveImg = (src: string) => {
            if (!src) return '';
            let s = src.trim().replace(/^['"]|['"]$/g, '');
            if (!s) return '';
            if (s.startsWith('http')) return s;
            if (s.startsWith('/images/remote/')) s = s.replace('/images/remote/', '');
            else if (s.startsWith('/images/')) s = s.replace('/images/', '');
            else if (s.startsWith('/api/media/')) s = s.replace('/api/media/', '');
            else if (s.startsWith('/')) s = s.substring(1);
            const sep = s.includes('?') ? '&' : '?';
            return `/api/media/${s}${sep}v=2`;
        };

        for (const m of meta) {
            const url = typeof m?.url === 'string' ? m.url.trim().replace(/^['"]|['"]$/g, '') : '';
            if (!url) continue;
            const alt = typeof m?.alt === 'string' ? m.alt : '';
            altByUrl.set(url.toLowerCase(), alt);
        }
        for (const u of urls) {
            const url = typeof u === 'string' ? u.trim().replace(/^['"]|['"]$/g, '') : '';
            if (!url) continue;
            out.push({ url: resolveImg(url), alt: resolveAltText(altByUrl.get(url.toLowerCase()), url, false) });
        }
        if (out.length > 0) return out;
        const rawFeaturedImage = post.data?.featured_media?.url as string | undefined;
        const hasFeatured = typeof rawFeaturedImage === 'string' && rawFeaturedImage.trim().length > 0;
        const featured = hasFeatured ? rawFeaturedImage.trim().replace(/^['"]|['"]$/g, '') : '';
        return featured ? [{ url: resolveImg(featured), alt: resolveAltText(String(post.title || ''), featured, false) }] : [];
    })();
    const featuredImage = carouselImages.length > 0 ? carouselImages[0].url : null;
    const featuredVideoId = (() => {
        const directId = typeof post.data?.featured_media?.video_id === 'string' ? post.data.featured_media.video_id.trim() : '';
        if (directId && /^[a-zA-Z0-9_-]{11}$/.test(directId)) return directId;
        const url = typeof post.data?.featured_media?.video_url === 'string' ? post.data.featured_media.video_url : '';
        return extractYouTubeVideoId(url);
    })();
    const categories = post.data?.taxonomy?.categories || [];
    const tagsRaw = post.data?.taxonomy?.tags;
    const tags: string[] = (() => {
        const out: string[] = [];
        const seen = new Set<string>();
        const add = (t: string) => {
            const v = t.trim();
            if (!v) return;
            const k = v.toLowerCase();
            if (seen.has(k)) return;
            seen.add(k);
            out.push(v);
        };
        if (typeof tagsRaw === 'string') {
            tagsRaw.split(',').forEach(add);
            return out;
        }
        if (Array.isArray(tagsRaw)) {
            tagsRaw.forEach((t: any) => {
                if (typeof t === 'string') add(t);
                else if (t && typeof t === 'object') add(t.name || t.slug || '');
            });
            return out;
        }
        return out;
    })();

    const canEdit = (() => {
        if (!enableEdit) return false;
        const base = post.published_at ? new Date(post.published_at as any).getTime() : NaN;
        if (!Number.isFinite(base)) return false;
        return Date.now() - base <= 24 * 60 * 60 * 1000;
    })();
    const canManage = !!enableEdit;

    const categoryMatch = categoryInput.match(/#([a-z0-9-]{1,64})$/i);
    const categoryQuery = categoryMatch ? categoryMatch[1].toLowerCase() : '';
    const categorySuggestions =
        categoryQuery.length > 0
            ? allCategories
                .filter((c) => {
                    const slug = String(c.slug || '').toLowerCase();
                    const name = String(c.name || '').toLowerCase();
                    const already = editCategories.some((x) => String(x.slug || '').toLowerCase() === slug);
                    if (already) return false;
                    return slug.startsWith(categoryQuery) || name.includes(categoryQuery);
                })
                .slice(0, 8)
            : [];

    const openEdit = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setEditError(null);
        setCategoryInput('');
        setEditTitle(String(post.title || ''));
        setEditBody(String(post.data?.content?.body || post.excerpt || ''));
        setEditFeaturedImageUrl(featuredImage);
        setEditCategories(() => {
            const raw = post.data?.taxonomy?.categories;
            if (!Array.isArray(raw)) return [];
            const out: Array<{ slug: string; name: string }> = [];
            const seen = new Set<string>();
            for (const c of raw) {
                const slug = typeof c?.slug === 'string' ? c.slug.trim() : '';
                if (!slug) continue;
                const key = slug.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                const name = typeof c?.name === 'string' && c.name.trim().length > 0 ? c.name.trim() : slug;
                out.push({ slug, name });
            }
            return out;
        });
        setIsEditOpen(true);
    };

    const handleEditImageClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        editFileInputRef.current?.click();
    };

    const handleEditImageFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setEditError(null);
        setIsUploadingEditImage(true);

        try {
            const file = files[0];
            const formData = new FormData();
            formData.append('file', file);

            const res = await fetch('/api/upload', {
                method: 'POST',
                body: formData,
            });

            if (!res.ok) {
                setEditError('Image upload failed. Please try again.');
                return;
            }

            const json = (await res.json()) as { success?: boolean; url?: string };
            if (json?.success && json.url) {
                setEditFeaturedImageUrl(json.url);
            } else {
                setEditError('Image upload failed. Please try again.');
            }
        } catch {
            setEditError('Image upload failed. Please try again.');
        } finally {
            setIsUploadingEditImage(false);
            event.target.value = '';
        }
    };

    const handleSaveEdit = async () => {
        setEditError(null);
        const nextTitle = editTitle.trim();
        const nextBody = editBody.trim();

        if (!nextTitle || !nextBody) {
            setEditError('Please add a title and a short description for your post.');
            return;
        }

        setIsSavingEdit(true);
        try {
            const res = await updateAuthorPost({
                id: post.id,
                title: nextTitle,
                body: nextBody,
                featuredImageUrl: editFeaturedImageUrl,
                categories: editCategories,
            });

            if (!res?.success) {
                setEditError(res?.error || 'Update failed. Please try again.');
                return;
            }

            setIsEditOpen(false);
            router.refresh();
        } catch {
            setEditError('Update failed. Please try again.');
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleDeletePost = async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setMoreMenuOpen(false);
        const ok = window.confirm('Delete this post? This cannot be undone.');
        if (!ok) return;
        try {
            const res = await deleteAuthorPost(post.id);
            if (!res?.success) {
                window.alert(res?.error || 'Delete failed. Please try again.');
                return;
            }
            router.refresh();
        } catch {
            window.alert('Delete failed. Please try again.');
        }
    };

    // Check local storage for liked status on mount & Handle Impressions
    useEffect(() => {
        const likedPosts = JSON.parse(localStorage.getItem('liked_posts') || '[]');
        if (likedPosts.includes(post.id)) {
            setIsLiked(true);
        }

        // Click outside to close share menu
        const handleClickOutside = (event: MouseEvent) => {
            const t = event.target as Node;
            const inTop = !!topShareMenuRef.current && topShareMenuRef.current.contains(t);
            const inFooter = !!footerShareMenuRef.current && footerShareMenuRef.current.contains(t);
            const inMore = !!topMoreMenuRef.current && topMoreMenuRef.current.contains(t);
            if (!inTop && !inFooter) setShareMenu(null);
            if (!inMore) setMoreMenuOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [post.id]);

    useEffect(() => {
        if (!isEditOpen) return;
        if (allCategories.length > 0) return;

        let active = true;
        setIsLoadingCategories(true);
        getAllCategoriesForEditor()
            .then((cats) => {
                if (!active) return;
                setAllCategories(Array.isArray(cats) ? cats : []);
            })
            .catch(() => {
                if (!active) return;
                setAllCategories([]);
            })
            .finally(() => {
                if (!active) return;
                setIsLoadingCategories(false);
            });

        return () => {
            active = false;
        };
    }, [isEditOpen, allCategories.length]);

    // Handle Impression increment when in view
    useEffect(() => {
        if (inView) {
            const impressionKey = `seen_${post.id}`;
            if (!sessionStorage.getItem(impressionKey)) {
                sessionStorage.setItem(impressionKey, 'true');
                incrementImpression(post.id);
                setImpressions(i => i + 1);
            }
        }
    }, [inView, post.id]);

    // Safety check for date
    let dateStr = 'Recently';
    if (post.published_at) {
        try {
            dateStr = format(new Date(post.published_at), 'MMMM d, yyyy');
        } catch (e) {
            console.error('Invalid date', post.published_at);
        }
    }

    // Determine label to show in pill (passed prop fallback to safe default)
    const label = categoryLabel || 'Article';

    const handleExpand: React.MouseEventHandler = (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;

        if (
            (topShareMenuRef.current && topShareMenuRef.current.contains(e.target as Node)) ||
            (footerShareMenuRef.current && footerShareMenuRef.current.contains(e.target as Node)) ||
            (topMoreMenuRef.current && topMoreMenuRef.current.contains(e.target as Node))
        ) {
            return;
        }

        e.preventDefault();
        setIsOpen(true);
        
        if (typeof window !== 'undefined') {
            window.history.pushState({ postId: post.id }, '', `/${post.slug}`);
        }

        void (async () => {
            try {
                const allowed = await getCanCurrentSessionComment();
                setCanComment(allowed);
            } catch {
                setCanComment(false);
            }
        })();

        const viewedKey = `viewed_${post.id}`;
        if (!sessionStorage.getItem(viewedKey)) {
            sessionStorage.setItem(viewedKey, 'true');
            incrementView(post.id);
            setViews(v => v + 1);
        }

        sendGAEvent('event', 'select_content', {
            content_type: 'article_card',
            item_id: post.id,
            item_name: post.title
        });

        if (typeof window !== 'undefined' && (window as any).dataLayer) {
            (window as any).dataLayer.push({
                event: 'virtual_page_view',
                page_location: `/${post.slug}`,
                page_title: post.title,
                page_path: `/${post.slug}`
            });
        }

        if (!content && !loading) {
            setLoading(true);
            void (async () => {
                try {
                    const html = await getPostContentById(post.id);
                    const enhanced = html ? enhanceHtmlOutput(html) : '';
                    setContent(enhanced || '<p>Content unavailable.</p>');
                } catch (err) {
                    console.error(err);
                    setContent('<p>Error loading content.</p>');
                } finally {
                    setLoading(false);
                }
            })();
        }
    };

    // Close accordion if hardware 'Back' button is pressed
    useEffect(() => {
        if (!isOpen) return;
        const handlePopState = () => { setIsOpen(false); };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isOpen]);

    const handleLike = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const newIsLiked = !isLiked;

        // Optimistic UI
        setIsLiked(newIsLiked);
        setLikes(prev => newIsLiked ? prev + 1 : prev - 1);

        // Persist to LocalStorage
        const likedPosts = JSON.parse(localStorage.getItem('liked_posts') || '[]');
        if (newIsLiked) {
            likedPosts.push(post.id);
        } else {
            const index = likedPosts.indexOf(post.id);
            if (index > -1) likedPosts.splice(index, 1);
        }
        localStorage.setItem('liked_posts', JSON.stringify(likedPosts));

        // Sync to Server
        await toggleLike(post.id, newIsLiked);
    };

    const handleShare = async (e: React.MouseEvent, where: 'top' | 'footer') => {
        e.preventDefault();
        e.stopPropagation();

        // GA4 Track Share Start
        sendGAEvent('event', 'share', {
            method: 'native_or_menu',
            content_type: 'article',
            item_id: post.id,
        });

        setShareMenu((prev) => (prev === where ? null : where));
    };

    const copyLink = (e: React.MouseEvent) => {
        e.stopPropagation();
        const shareUrl = `${window.location.origin}/${post.slug}`;
        navigator.clipboard.writeText(shareUrl);

        sendGAEvent('event', 'share', {
            method: 'copy_link',
            content_type: 'article',
            item_id: post.id,
        });

        setCopied(true);
        incrementShare(post.id);
        setShares(s => s + 1);
        setTimeout(() => {
            setCopied(false);
            setShareMenu(null);
        }, 1500);
    };

    const shareInstagram = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const shareUrl = `${window.location.origin}/${post.slug}`;
        await navigator.clipboard.writeText(shareUrl);
        incrementShare(post.id);
        setShares(s => s + 1);
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
            setShareMenu(null);
        }, 1500);
        window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    };

    const handleReport = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const res = await reportContent(Number(post.id));
        if (!res.success) {
            alert(res.error || 'Unable to report.');
            return;
        }
        if (res.alreadyReported) {
            alert('Already reported.');
        } else {
            alert('Reported.');
        }
        setShareMenu(null);
    };

    const getShareLinks = () => {
        if (typeof window === 'undefined') return { fb: '#', tw: '#', li: '#' };
        const u = encodeURIComponent(`${window.location.origin}/${post.slug}`);
        const t = encodeURIComponent(post.title);
        return {
            fb: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
            tw: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
            li: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`
        };
    };

    const links = getShareLinks();

    return (
        <div
            ref={inViewRef}
            className={`group flex flex-col bg-surface-elevated border-y sm:border border-border-subtle rounded-none overflow-hidden hover:border-brand transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] ${className}`}
        >
            {featuredVideoId ? (
                <div className="block aspect-[16/10] overflow-hidden bg-surface-inset relative shrink-0">
                    {inView ? (
                        <iframe
                            src={buildYouTubeEmbedUrl(featuredVideoId)}
                            title={post.title}
                            className="absolute inset-0 w-full h-full"
                            allow="encrypted-media; picture-in-picture"
                            referrerPolicy="strict-origin-when-cross-origin"
                            loading="lazy"
                            allowFullScreen
                        />
                    ) : (
                        <Image
                            src={`https://i.ytimg.com/vi/${featuredVideoId}/hqdefault.jpg`}
                            alt={post.title}
                            className="w-full h-full object-cover"
                            fill
                            sizes="(max-width: 768px) 100vw, 50vw"
                        />
                    )}

                    <div className="absolute top-4 right-4 flex gap-2 z-10">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10" title="Impressions (Seen in Feed)">
                            <Activity size={12} />
                            <span>{impressions.toLocaleString('en-US')}</span>
                        </div>

                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10" title="Reads (Clicked)">
                            <Eye size={12} />
                            <span>{views.toLocaleString('en-US')}</span>
                        </div>
                        <button
                            type="button"
                            onClick={handleLike}
                            aria-pressed={isLiked}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md text-[10px] font-bold border transition-all ${isLiked ? 'bg-brand text-white border-brand' : 'bg-black/40 text-white/90 border-white/10 hover:bg-white/10'}`}
                            title="Likes"
                        >
                            <Heart size={12} className={isLiked ? 'fill-current' : ''} />
                            <span>{likes.toLocaleString('en-US')}</span>
                        </button>
                        <div className="relative" ref={topShareMenuRef}>
                            <button
                                type="button"
                                onClick={(e) => handleShare(e, 'top')}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md text-[10px] font-bold border transition-all ${isLiked ? 'bg-brand text-white border-brand' : 'bg-black/40 text-white/90 border-white/10 hover:bg-white/10"
                                title="Shares"
                            >
                                <Share2 size={12} />
                                <span className="tabular-nums">{Math.max(0, shares).toLocaleString('en-US')}</span>
                            </button>
                            {shareMenu === 'top' && (
                                <div className="absolute top-full right-0 mt-2 p-2 bg-surface-elevated border border-border-subtle rounded-xl shadow-2xl flex gap-1 z-30 animate-fade-in whitespace-nowrap">
                                <a
                                    href={links.fb}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-surface-inset rounded-lg text-blue-500"
                                    title="Share on Facebook"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        sendGAEvent('event', 'share', { method: 'facebook', content_type: 'article', item_id: post.id });
                                    }}
                                >
                                    <Facebook size={18} />
                                </a>
                                <a
                                    href={links.tw}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-surface-inset rounded-lg text-sky-400"
                                    title="Share on Twitter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        sendGAEvent('event', 'share', { method: 'twitter', content_type: 'article', item_id: post.id });
                                    }}
                                >
                                    <Twitter size={18} />
                                </a>
                                <a
                                    href={links.li}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-surface-inset rounded-lg text-blue-700"
                                    title="Share on LinkedIn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        sendGAEvent('event', 'share', { method: 'linkedin', content_type: 'article', item_id: post.id });
                                    }}
                                >
                                    <Linkedin size={18} />
                                </a>
                                <button
                                    type="button"
                                    onClick={shareInstagram}
                                    className="p-2 hover:bg-surface-inset rounded-lg text-pink-500"
                                    title="Share on Instagram"
                                >
                                    <Instagram size={18} />
                                </button>
                                <div className="w-px bg-border-subtle mx-1 my-1"></div>
                                <button
                                    onClick={copyLink}
                                    className="p-2 hover:bg-surface-inset rounded-lg text-text-primary relative"
                                    title="Copy Link"
                                >
                                    {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                </button>
                                <div className="w-px bg-border-subtle mx-1 my-1"></div>
                                <button
                                    type="button"
                                    onClick={handleReport}
                                    className="p-2 hover:bg-surface-inset rounded-lg text-red-400"
                                    title="Report"
                                >
                                    <Flag size={18} />
                                </button>
                                </div>
                            )}
                        </div>
                        {canManage && (
                            <div className="relative" ref={topMoreMenuRef}>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShareMenu(null);
                                        setMoreMenuOpen((v) => !v);
                                    }}
                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10 hover:bg-white/10 min-w-[64px] justify-center"
                                    aria-label="More options"
                                    title="More"
                                >
                                    <MoreVertical size={12} />
                                </button>
                                {moreMenuOpen && (
                                    <div className="absolute top-full right-0 mt-2 w-40 bg-[var(--color-bg-base)] border border-border-subtle rounded-xl shadow-2xl overflow-hidden z-30 animate-fade-in">
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-sm text-[var(--color-text-main)] hover:bg-surface-inset"
                                                onClick={(e) => {
                                                    setMoreMenuOpen(false);
                                                    openEdit(e);
                                                }}
                                            >
                                                Edit
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-surface-inset"
                                            onClick={handleDeletePost}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : featuredImage ? (
                <div className="block aspect-[16/10] overflow-hidden bg-surface-inset relative shrink-0">
                    <ImageCarousel
                        images={carouselImages}
                        linkHref={`/${post.slug}`}
                        onLinkClick={handleExpand}
                        className="w-full h-full"
                        imageClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 z-[1] bg-gradient-to-t from-surface-elevated to-transparent opacity-60 pointer-events-none"></div>

                    <div className="absolute top-4 right-4 flex gap-2 z-10">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10" title="Impressions (Seen in Feed)">
                            <Activity size={12} />
                            <span>{impressions.toLocaleString('en-US')}</span>
                        </div>

                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10" title="Reads (Clicked)">
                            <Eye size={12} />
                            <span>{views.toLocaleString('en-US')}</span>
                        </div>
                        <button
                            type="button"
                            onClick={handleLike}
                            aria-pressed={isLiked}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md text-[10px] font-bold border transition-all ${isLiked ? 'bg-brand text-white border-brand' : 'bg-black/40 text-white/90 border-white/10 hover:bg-white/10'}`}
                            title="Likes"
                        >
                            <Heart size={12} className={isLiked ? 'fill-current' : ''} />
                            <span>{likes.toLocaleString('en-US')}</span>
                        </button>
                        <div className="relative" ref={topShareMenuRef}>
                            <button
                                type="button"
                                onClick={(e) => handleShare(e, 'top')}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10 hover:bg-white/10 min-w-[64px] justify-center"
                                title="Shares"
                            >
                                <Share2 size={12} />
                                <span className="tabular-nums">{Math.max(0, shares).toLocaleString('en-US')}</span>
                            </button>
                            {shareMenu === 'top' && (
                                <div className="absolute top-full right-0 mt-2 p-2 bg-surface-elevated border border-border-subtle rounded-xl shadow-2xl flex gap-1 z-30 animate-fade-in whitespace-nowrap">
                                <a
                                    href={links.fb}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-surface-inset rounded-lg text-blue-500"
                                    title="Share on Facebook"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        sendGAEvent('event', 'share', { method: 'facebook', content_type: 'article', item_id: post.id });
                                    }}
                                >
                                    <Facebook size={18} />
                                </a>
                                <a
                                    href={links.tw}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-surface-inset rounded-lg text-sky-400"
                                    title="Share on Twitter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        sendGAEvent('event', 'share', { method: 'twitter', content_type: 'article', item_id: post.id });
                                    }}
                                >
                                    <Twitter size={18} />
                                </a>
                                <a
                                    href={links.li}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-surface-inset rounded-lg text-blue-700"
                                    title="Share on LinkedIn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        sendGAEvent('event', 'share', { method: 'linkedin', content_type: 'article', item_id: post.id });
                                    }}
                                >
                                    <Linkedin size={18} />
                                </a>
                                <button
                                    type="button"
                                    onClick={shareInstagram}
                                    className="p-2 hover:bg-surface-inset rounded-lg text-pink-500"
                                    title="Share on Instagram"
                                >
                                    <Instagram size={18} />
                                </button>
                                <div className="w-px bg-border-subtle mx-1 my-1"></div>
                                <button
                                    onClick={copyLink}
                                    className="p-2 hover:bg-surface-inset rounded-lg text-text-primary relative"
                                    title="Copy Link"
                                >
                                    {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                </button>
                                <div className="w-px bg-border-subtle mx-1 my-1"></div>
                                <button
                                    type="button"
                                    onClick={handleReport}
                                    className="p-2 hover:bg-surface-inset rounded-lg text-red-400"
                                    title="Report"
                                >
                                    <Flag size={18} />
                                </button>
                                </div>
                            )}
                        </div>
                        {canManage && (
                            <div className="relative" ref={topMoreMenuRef}>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShareMenu(null);
                                        setMoreMenuOpen((v) => !v);
                                    }}
                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-[10px] font-bold text-white/90 border border-white/10 hover:bg-white/10 min-w-[64px] justify-center"
                                    aria-label="More options"
                                    title="More"
                                >
                                    <MoreVertical size={12} />
                                </button>
                                {moreMenuOpen && (
                                    <div className="absolute top-full right-0 mt-2 w-40 bg-[var(--color-bg-base)] border border-border-subtle rounded-xl shadow-2xl overflow-hidden z-30 animate-fade-in">
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-sm text-[var(--color-text-main)] hover:bg-surface-inset"
                                                onClick={(e) => {
                                                    setMoreMenuOpen(false);
                                                    openEdit(e);
                                                }}
                                            >
                                                Edit
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-surface-inset"
                                            onClick={handleDeletePost}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            <div className="px-4 sm:px-6 py-6 flex flex-col flex-grow">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-brand">
                        <span>{label}</span>
                        <span className="w-1 h-1 rounded-full bg-border-subtle"></span>
                        <span className="text-text-secondary" suppressHydrationWarning>{dateStr}</span>
                    </div>
                    {canManage && !featuredImage && !featuredVideoId && (
                        <div className="relative" ref={topMoreMenuRef}>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShareMenu(null);
                                    setMoreMenuOpen((v) => !v);
                                }}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-inset text-[10px] font-bold text-text-primary border border-border-subtle hover:bg-surface-elevated min-w-[36px] justify-center"
                                aria-label="More options"
                                title="More"
                            >
                                <MoreVertical size={12} />
                            </button>
                            {moreMenuOpen && (
                                <div className="absolute top-full right-0 mt-2 w-40 bg-[var(--color-bg-base)] border border-border-subtle rounded-xl shadow-2xl overflow-hidden z-30 animate-fade-in">
                                    {canEdit && (
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm text-[var(--color-text-main)] hover:bg-surface-inset"
                                            onClick={(e) => {
                                                setMoreMenuOpen(false);
                                                openEdit(e);
                                            }}
                                        >
                                            Edit
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-surface-inset"
                                        onClick={handleDeletePost}
                                    >
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <h3 className="text-xl font-bold mb-3">
                    <Link
                        href={`/${post.slug}`}
                        className="text-text-primary hover:text-brand transition-colors"
                        onClick={handleExpand}
                    >
                        {post.title}
                    </Link>
                </h3>

                {/* Content Area - Slides Open */}
                <div className="relative">
                    {isOpen ? (
                        <div className="animate-fade-in space-y-6">
                            {loading && !content ? (
                                <div className="py-8 flex justify-center text-brand">
                                    <Loader2 className="animate-spin" size={24} />
                                </div>
                            ) : (
                                <>
                                    <div className="prose prose-sm max-w-none article-content prose-headings:text-[var(--color-text-main)] prose-p:text-[var(--color-text-main)] p-4 rounded-md">
                                        <div dangerouslySetInnerHTML={{ __html: content || '' }} />
                                    </div>

                                    {tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2 px-4">
                                            {tags.map((tag, i) => (
                                                <Link
                                                    key={`${tag}-${i}`}
                                                    href={`/articles?q=${encodeURIComponent(tag)}`}
                                                    className="px-2.5 py-1 rounded-full bg-surface-inset text-text-primary text-xs border border-border-subtle hover:bg-surface-elevated transition-colors"
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label={`View tag ${tag}`}
                                                >
                                                    {tag}
                                                </Link>
                                            ))}
                                        </div>
                                    )}

                                    <div className="border-t border-border-subtle pt-4 space-y-4">
                                        <div className="text-sm font-semibold text-[var(--color-text-main)]">
                                            Comments
                                        </div>
                                        <CommentsThread
                                            contentId={Number(post.id)}
                                            slug={post.slug}
                                            initialComments={[]}
                                            clientFetchOnMount
                                            canComment={canComment}
                                        />
                                    </div>

                                    <div className="mt-4 flex justify-center pt-4 border-t border-border-subtle">
                                        <button
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                setIsOpen(false); 
                                                if (typeof window !== 'undefined' && window.history.state?.postId === post.id) {
                                                    window.history.back();
                                                }
                                            }}
                                            className="text-xs font-bold uppercase tracking-wider text-text-secondary hover:text-brand flex items-center gap-1"
                                        >
                                            <X size={14} /> Close Article
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        (() => {
                            const excerpt = String(post.excerpt || '');
                            const hasHtmlTags = /<\/?[a-z][\s\S]*?>/i.test(excerpt);
                            if (hasHtmlTags) {
                                return (
                                    <div
                                        className="text-text-secondary text-sm line-clamp-3 mb-6 cursor-pointer hover:text-text-primary transition-colors"
                                        dangerouslySetInnerHTML={{ __html: excerpt }}
                                        onClick={handleExpand}
                                    />
                                );
                            }
                            return (
                                <div
                                    className="text-text-secondary text-sm line-clamp-3 mb-6 cursor-pointer hover:text-text-primary transition-colors"
                                    onClick={handleExpand}
                                >
                                    {excerpt}
                                </div>
                            );
                        })()
                    )}
                </div>

                {!isOpen && (
                    <div className="flex items-center gap-2 text-sm font-medium text-text-primary mt-auto pt-4 border-t border-border-subtle">
                        <div className="flex items-center gap-2">
                            {(() => {
                                const rawAvatar = typeof post.author_avatar_url === 'string' ? post.author_avatar_url.trim() : '';
                                const avatar = rawAvatar ? rawAvatar.replace(/^['"]|['"]$/g, '') : '';
                                const label = String(post.author_display_name || post.author_name || 'Author');
                                const initial = label.trim().slice(0, 1).toUpperCase() || 'A';
                                return avatar ? (
                                    <Image
                                        src={avatar}
                                        alt={label}
                                        className="w-8 h-8 rounded-full object-cover bg-[var(--bg-secondary)] border border-border-subtle"
                                        width={32}
                                        height={32}
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-[var(--color-primary)] flex items-center justify-center text-white font-bold text-xs uppercase">
                                        {initial}
                                    </div>
                                );
                            })()}
                            {(() => {
                                const authorHandle =
                                    typeof post.author_handle === 'string'
                                        ? post.author_handle.trim().toLowerCase().replace(/^@+/, '')
                                        : '';
                                return authorHandle ? (
                                    <Link href={`/u/${authorHandle}`} className="whitespace-nowrap hover:underline">
                                    {post.author_display_name || post.author_name || 'CarSite Editor'}
                                    </Link>
                                ) : (
                                    <span className="whitespace-nowrap">{post.author_display_name || post.author_name || 'CarSite Editor'}</span>
                                );
                            })()}
                        </div>

                        <div className="ml-auto flex items-center gap-3 relative" ref={footerShareMenuRef}>
                            {/* Share Button (Footer) */}
                            <button
                                onClick={(e) => handleShare(e, 'footer')}
                                className="flex items-center gap-1.5 transition-colors text-text-secondary hover:text-brand"
                                aria-label="Share"
                            >
                                <Share2 size={16} />
                                <span className="text-xs font-semibold tabular-nums">{Math.max(0, shares).toLocaleString('en-US')}</span>
                            </button>

                            {/* Like Button (Footer) */}
                            <button
                                type="button"
                                onClick={handleLike}
                                aria-pressed={isLiked}
                                className={`flex items-center gap-1.5 transition-colors ${isLiked ? 'text-brand' : 'text-text-secondary hover:text-brand'}`}
                                aria-label="Like this article"
                                title="Likes"
                            >
                                <Heart size={16} className={isLiked ? 'fill-current' : ''} />
                                <span className="text-xs font-semibold">{likes.toLocaleString('en-US')}</span>
                            </button>

                            {/* SHARE POPUP MENU */}
                            {shareMenu === 'footer' && (
                                <div className="absolute bottom-full bg-[var(--color-bg-surface)] right-0 mb-2 p-2 bg-surface-elevated border border-border-subtle rounded-xl shadow-2xl flex gap-1 z-30 animate-fade-in whitespace-nowrap">
                                    <a
                                        href={links.fb}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 hover:bg-surface-inset rounded-lg text-blue-500"
                                        title="Share on Facebook"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            sendGAEvent('event', 'share', { method: 'facebook', content_type: 'article', item_id: post.id });
                                        }}
                                    >
                                        <Facebook size={18} />
                                    </a>
                                    <a
                                        href={links.tw}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 hover:bg-surface-inset rounded-lg text-sky-400"
                                        title="Share on Twitter"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            sendGAEvent('event', 'share', { method: 'twitter', content_type: 'article', item_id: post.id });
                                        }}
                                    >
                                        <Twitter size={18} />
                                    </a>
                                    <a
                                        href={links.li}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 hover:bg-surface-inset rounded-lg text-blue-700"
                                        title="Share on LinkedIn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            sendGAEvent('event', 'share', { method: 'linkedin', content_type: 'article', item_id: post.id });
                                        }}
                                    >
                                        <Linkedin size={18} />
                                    </a>
                                    <button
                                        type="button"
                                        onClick={shareInstagram}
                                        className="p-2 hover:bg-surface-inset rounded-lg text-pink-500"
                                        title="Share on Instagram"
                                    >
                                        <Instagram size={18} />
                                    </button>
                                    <div className="w-px bg-border-subtle mx-1 my-1"></div>
                                    <button
                                        onClick={copyLink}
                                        className="p-2 hover:bg-surface-inset rounded-lg text-text-primary relative"
                                        title="Copy Link"
                                    >
                                        {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                    </button>
                                    <div className="w-px bg-border-subtle mx-1 my-1"></div>
                                    <button
                                        type="button"
                                        onClick={handleReport}
                                        className="p-2 hover:bg-surface-inset rounded-lg text-red-400"
                                        title="Report"
                                    >
                                        <Flag size={18} />
                                    </button>
                                </div>
                            )}

                            {categories.length > 0 && (
                                <div className="flex flex-wrap gap-2 border-l border-border-subtle pl-4">
                                    {categories.slice(0, 2).map((cat: any) => {
                                        const parent = MAJOR_BRANDS.find(brand => cat.slug.startsWith(`${brand}-`));
                                        const href = parent ? `/${parent}/${cat.slug}` : `/${cat.slug}`;
                                        return (
                                            <Link
                                                key={cat.slug}
                                                href={href}
                                                className="text-[var(--color-text-main)] hover:text-[var(--color-primary)] text-xs transition-colors"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                #{cat.name}
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {isEditOpen && (
                <div
                    className="fixed inset-0 z-[200] flex items-center justify-center p-4"
                    onMouseDown={() => setIsEditOpen(false)}
                >
                    <div className="absolute inset-0 bg-black/60" />
                    <div
                        className="relative w-full max-w-xl rounded-2xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-2xl"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="p-5 border-b border-[var(--color-border)] flex items-center gap-3">
                            <div className="text-lg font-bold text-[var(--color-text-main)]">Edit post</div>
                            <button
                                type="button"
                                className="ml-auto p-2 rounded-lg hover:bg-[var(--bg-secondary)] text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] transition-colors"
                                onClick={() => setIsEditOpen(false)}
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            {editError && (
                                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                                    {editError}
                                </div>
                            )}

                            <div className="space-y-2">
                                <div className="text-sm font-semibold text-[var(--color-text-main)]">Image</div>
                                <div className="flex items-center gap-3">
                                    <div className="w-24 h-16 rounded-lg overflow-hidden bg-[var(--bg-secondary)] border border-[var(--color-border)] shrink-0 relative">
                                        {editFeaturedImageUrl ? (
                                            <Image src={editFeaturedImageUrl} alt="" className="w-full h-full object-cover" fill />
                                        ) : (
                                            <div className="w-full h-full" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            ref={editFileInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleEditImageFileChange}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleEditImageClick}
                                            disabled={isUploadingEditImage}
                                            className="px-3 py-2 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] border border-[var(--color-border)] text-sm font-semibold text-[var(--color-text-main)] disabled:opacity-60"
                                        >
                                            {isUploadingEditImage ? 'Uploading…' : 'Change image'}
                                        </button>
                                        {editFeaturedImageUrl && (
                                            <button
                                                type="button"
                                                onClick={() => setEditFeaturedImageUrl(null)}
                                                className="px-3 py-2 rounded-lg bg-transparent hover:bg-[var(--bg-secondary)] border border-[var(--color-border)] text-sm font-semibold text-[var(--color-text-main)]"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="text-sm font-semibold text-[var(--color-text-main)]">
                                    <label htmlFor={`edit-title-${post.id}`}>Title</label>
                                </div>
                                <input
                                    id={`edit-title-${post.id}`}
                                    name="edit_title"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-main)]"
                                    placeholder="Title"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="text-sm font-semibold text-[var(--color-text-main)]">
                                    <label htmlFor={`edit-body-${post.id}`}>Description</label>
                                </div>
                                <textarea
                                    id={`edit-body-${post.id}`}
                                    name="edit_body"
                                    value={editBody}
                                    onChange={(e) => setEditBody(e.target.value)}
                                    rows={5}
                                    className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-main)] resize-none"
                                    placeholder="Write a short description"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="text-sm font-semibold text-[var(--color-text-main)]">
                                    <label htmlFor={`edit-category-${post.id}`}>Categories</label>
                                </div>
                                <div className="relative">
                                    <input
                                        id={`edit-category-${post.id}`}
                                        name="edit_category"
                                        value={categoryInput}
                                        onChange={(e) => setCategoryInput(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-main)]"
                                        placeholder="#type to search categories"
                                    />
                                    {(isLoadingCategories || categorySuggestions.length > 0) && categoryQuery.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-2 rounded-xl border border-[var(--color-border)] bg-[var(--bg-elevated)] shadow-xl overflow-hidden z-[210]">
                                            {isLoadingCategories ? (
                                                <div className="px-3 py-2 text-sm text-[var(--color-text-muted)]">Loading…</div>
                                            ) : (
                                                categorySuggestions.map((c) => (
                                                    <button
                                                        key={c.slug}
                                                        type="button"
                                                        className="w-full text-left px-3 py-2 hover:bg-[var(--bg-secondary)] text-sm text-[var(--color-text-main)]"
                                                        onClick={() => {
                                                            setEditCategories((prev) => {
                                                                const key = String(c.slug || '').toLowerCase();
                                                                if (!key) return prev;
                                                                if (prev.some((x) => String(x.slug || '').toLowerCase() === key)) return prev;
                                                                return [...prev, { slug: c.slug, name: c.name }];
                                                            });
                                                            setCategoryInput('');
                                                        }}
                                                    >
                                                        #{c.name}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>

                                {editCategories.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {editCategories.map((c) => (
                                            <button
                                                key={c.slug}
                                                type="button"
                                                onClick={() => setEditCategories((prev) => prev.filter((x) => x.slug !== c.slug))}
                                                className="px-2.5 py-1 rounded-full bg-[var(--bg-secondary)] border border-[var(--color-border)] text-xs font-semibold text-[var(--color-text-main)] hover:bg-[var(--bg-tertiary)]"
                                                title="Remove category"
                                            >
                                                #{c.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-5 border-t border-[var(--color-border)] flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setIsEditOpen(false)}
                                className="px-4 py-2 rounded-lg bg-transparent hover:bg-[var(--bg-secondary)] border border-[var(--color-border)] text-sm font-semibold text-[var(--color-text-main)]"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveEdit}
                                disabled={isSavingEdit || isUploadingEditImage}
                                className="ml-auto px-4 py-2 rounded-lg bg-[var(--color-primary)] hover:opacity-90 text-white text-sm font-semibold disabled:opacity-60"
                            >
                                {isSavingEdit ? 'Saving…' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
